// Qué está haciendo un agente, dicho en una línea.
//
// EL DEFECTO QUE ESTO ARREGLA: un revisor salía siempre «sin tarea en
// ejecución». No es un error de lectura, es que el revisor NO TOMA TAREAS: la
// cola entrega trabajo a los esclavos, y `work` de un puesto se arma con
// `agent_attempts`, que para un maestro está vacío por diseño. Decir «sin
// tarea» ahí es contestar otra pregunta.
//
// LO QUE SÍ SE PUEDE SABER HOY, sin tocar Core: la bandeja de atención
// (`GET /admin/attention`) publica, por cada ronda abierta que todavía no se
// puede decidir, el PUESTO de su reserva viva (`scope.position_id`), con la
// tarea, el número de ronda, la vigencia de la reserva y cuántos criterios
// lleva dictaminados. Eso es exactamente «qué está revisando este puesto».
//
// LO QUE NO SE PUEDE SABER, y por eso no se afirma:
//
//   · RESERVAR NO ES ESTAR EJECUTANDO. Core registra que ese puesto se quedó
//     con la ronda; que el proceso local haya arrancado no viaja por ninguna
//     ruta. La tarjeta dice «revisión reservada», nunca «revisando».
//   · NO VERLA NO ES ESTAR LIBRE. La bandeja solo trae las rondas que todavía
//     NO se pueden decidir; una ronda con todos sus criterios dictaminados sale
//     de la lista aunque el revisor siga trabajando en ella. Por eso la
//     ausencia se dice como ausencia de dato, no como ociosidad.
//   · UNA RESERVA VENCIDA NO PRUEBA QUE EL PROCESO MURIÓ. Dice que el derecho a
//     publicar sobre esa ronda se pasó de hora.
//
// Puro: sin React y sin red, probado sin navegador.

export const ACTIVITY = {
    running:         'ejecutando',
    runningStale:    'vigencia_vencida',
    reserved:        'revision_reservada',
    reservedExpired: 'reserva_vencida',
    reviewerUnknown: 'revision_sin_publicar',
    idle:            'sin_trabajo',
};

// Las rondas reservadas, por puesto. Una por puesto: si hubiera dos —no
// debería—, gana la entrega más reciente.
export function reviewWorkByPosition(items) {
    const byPosition = new Map();

    for (const item of items ?? []) {
        if (item?.kind !== 'revision_incompleta') continue;

        const positionId = item.scope?.position_id;
        // Sin puesto no hay reserva viva de un agente: la ronda está abierta y
        // nadie la tomó, o la tomó una persona desde el panel.
        if (positionId == null) continue;

        const detail = item.happened?.detail ?? {};
        const found = {
            positionId,
            taskId: item.scope?.task_id ?? null,
            taskTitle: detail.task_title ?? null,
            roundId: item.resource?.id ?? null,
            roundNumber: detail.round_number ?? null,
            reservationId: detail.reservation_id ?? null,
            reviewerId: detail.reserved_by ?? null,
            // `since` de la incidencia es cuándo se ABRIÓ la ronda, o sea la
            // entrega. Cuándo se reservó no viaja.
            deliveredAt: item.happened?.since ?? null,
            leaseUntil: item.validity?.expires_at ?? null,
            expired: item.validity?.state === 'vencida' || item.validity?.expired === true,
            evaluated: detail.evaluated ?? null,
            criteriaTotal: detail.criteria_total ?? null,
            unverified: detail.unverified ?? null,
            reason: item.reason ?? null,
        };

        const current = byPosition.get(positionId);
        if (!current || isNewer(found.deliveredAt, current.deliveredAt)) {
            byPosition.set(positionId, found);
        }
    }

    return byPosition;
}

function isNewer(a, b) {
    if (!a) return false;
    if (!b) return true;
    return new Date(a).getTime() > new Date(b).getTime();
}

// La línea de estado de un puesto. `review` es lo que `reviewWorkByPosition`
// encontró para ese puesto, o `undefined`.
export function readAgentActivity(row, review) {
    const work = row?.work ?? {};
    const isReviewer = row?.seat?.role === 'agent_master';

    // UNA CORRIDA MANDA SOBRE TODO LO DEMÁS: es la única evidencia de que el
    // puesto está ejecutando algo, porque la abre y la cierra quien ejecuta.
    if (work.attempt) {
        const stale = work.state === 'vigencia_vencida' || work.stale_reason === 'vigencia_vencida';
        return {
            key: stale ? ACTIVITY.runningStale : ACTIVITY.running,
            headline: stale ? 'Corrida con la vigencia pasada' : 'Ejecutando una tarea',
            hint: stale
                ? 'La corrida sigue abierta y su vigencia se pasó: el resultado es incierto hasta que alguien concilie.'
                : 'La corrida está declarada abierta por el propio agente.',
            tone: stale ? 'warn' : 'accent',
            taskId: work.task?.id ?? work.attempt.task_id ?? null,
            taskTitle: work.task?.title ?? null,
            since: work.attempt.started_at ?? work.attempt.reservation_at ?? work.since ?? null,
            sinceLabel: 'Tomada',
            leaseUntil: work.attempt.lease_expires_at ?? null,
            unknown: false,
        };
    }

    if (review) {
        return {
            key: review.expired ? ACTIVITY.reservedExpired : ACTIVITY.reserved,
            headline: review.expired ? 'Reserva de revisión vencida' : 'Revisión reservada',
            hint: review.expired
                ? 'El derecho a publicar sobre esa ronda se pasó de hora. No prueba que el proceso haya muerto.'
                : 'Core registra que este puesto se quedó con la ronda. Que el proceso local haya arrancado no viaja por ninguna ruta.',
            tone: review.expired ? 'warn' : 'accent',
            taskId: review.taskId,
            taskTitle: review.taskTitle,
            roundNumber: review.roundNumber,
            since: review.deliveredAt,
            sinceLabel: 'Entregada',
            leaseUntil: review.leaseUntil,
            evaluated: review.evaluated,
            criteriaTotal: review.criteriaTotal,
            unknown: false,
        };
    }

    if (isReviewer) {
        return {
            key: ACTIVITY.reviewerUnknown,
            headline: 'Sin revisión publicada',
            hint: 'Un revisor no toma tareas de la cola, y la bandeja solo publica las rondas que todavía no se pueden decidir. Esto NO dice que esté libre.',
            tone: 'neutral',
            unknown: true,
        };
    }

    return {
        key: ACTIVITY.idle,
        headline: 'Sin tarea tomada',
        hint: 'No hay ninguna corrida abierta para este puesto.',
        tone: 'neutral',
        unknown: false,
    };
}

// Las tres horas que el documento pide separar, porque contestan preguntas
// distintas y mezclarlas fue parte de la confusión:
//
//   · la última señal REGISTRADA del puesto (`last_signal`) — una orden, una
//     corrida: algo que quedó escrito;
//   · la última comunicación del EQUIPO (`presence.last_seen_at`), que es la
//     marca durable de la máquina;
//   · y el canal en memoria de este proceso de Core (`presence.state`), que no
//     es una hora.
const SIGNAL_SOURCE = {
    intento: 'una corrida',
    orden: 'una orden',
    entrega: 'una entrega',
    presencia: 'la presencia del equipo',
};

export function readSignals(row) {
    const signal = row?.last_signal ?? null;
    return {
        last: signal?.at ?? null,
        source: signal ? (SIGNAL_SOURCE[signal.source] ?? signal.source) : null,
        durable: signal?.durable ?? null,
        machineSeen: row?.presence?.last_seen_at ?? null,
        connections: row?.presence?.connections ?? 0,
    };
}

// Cuántos de los criterios de la ronda llevan dictamen. NO es un porcentaje de
// avance: los criterios no pesan igual y un agente puede publicarlos todos al
// final.
export function criteriaProgress(activity) {
    if (activity?.criteriaTotal == null || activity?.evaluated == null) return null;
    return { evaluated: activity.evaluated, total: activity.criteriaTotal };
}
